import { v4 as uuidv4 } from 'uuid';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { updatePublicAPI } from '@/service/publicService';
import { createTmpUploadedImgs } from '../../../handler/ChatAIHandler';
import {
    validateImagesOnly,
    isValidImageFile,
    VALID_IMAGE_TYPES,
    VALID_IMAGE_ACCEPT,
    MAX_UPLOAD_COUNT,
    MAX_FILE_SIZE
} from './UploadValidationHandler';

/**
 * Validate upload images using strict image validation
 * @param {File[]} images - List of images to validate
 * @param {Object[]} currentUploadImgs - Current uploaded images
 * @param {Object[]} currentUploadFiles - Current uploaded files
 * @param {Function} enqueueSnackbar - Snackbar function for showing errors
 * @returns {boolean} - true if valid, false otherwise
 */
const validateUploadImages = (images = [], currentUploadImgs = [], currentUploadFiles = [], enqueueSnackbar) => {
    const result = validateImagesOnly({
        newFiles: images,
        currentUploadImgs,
        currentUploadFiles,
        enqueueSnackbar
    });

    return result.isValid && result.imageFiles.length > 0;
};

/**
 * Create temporary image objects from files
 * @param {File[]} files - List of files
 * @returns {Object[]} - List of image objects with id, localUrl, fileData
 */
const createTmpImageObjects = (files) => {
    return files.map(file => ({
        id: `tmp_attach_${uuidv4()}`,
        localUrl: URL.createObjectURL(file),
        fileData: file
    }));
};

/**
 * Handle drop images (drag & drop)
 * @param {File[]} acceptedFiles - Accepted files from dropzone
 * @param {Object} params - Parameters
 * @param {boolean} params.canUploadImgToSendAi - Can upload image flag
 * @param {Object[]} params.uploadImgs - Current uploaded images
 * @param {Object[]} params.uploadFiles - Current uploaded files
 * @param {Function} params.setUploadImgs - Set uploaded images function
 * @param {Function} params.enqueueSnackbar - Snackbar function
 * @param {Object} params.corporationDetail - Corporation detail
 * @param {Object} params.projectInuseDetail - Project in use detail
 * @param {string} params.chatThreadId - Chat thread ID
 * @param {Function} params.setIsUploading - Set uploading state
 * @param {Object} params.inputRef - Input ref to focus after drop
 */
const handleDropImages = (acceptedFiles, params) => {
    const {
        canUploadImgToSendAi,
        uploadImgs,
        uploadFiles = [],
        setUploadImgs,
        enqueueSnackbar,
        corporationDetail,
        projectInuseDetail,
        chatThreadId,
        setIsUploading,
        inputRef
    } = params;

    if (!canUploadImgToSendAi) return;
    if (!acceptedFiles || Utils.isEmptyArray(acceptedFiles)) return;

    // Filter only image files first
    const imageFiles = acceptedFiles.filter(file => isValidImageFile(file));
    
    if (Utils.isEmptyArray(imageFiles)) return;

    const isValidImgs = validateUploadImages(imageFiles, uploadImgs, uploadFiles, enqueueSnackbar);
    if (!isValidImgs) return;

    const imgs = createTmpImageObjects(imageFiles);
    setUploadImgs(prev => [...prev, ...imgs]);

    if (inputRef?.current) {
        inputRef.current.focus();
    }

    createTmpUploadedImgs(
        corporationDetail.id,
        projectInuseDetail.id,
        chatThreadId,
        imgs,
        setUploadImgs,
        setIsUploading
    );
};

/**
 * Handle paste images from clipboard
 * @param {ClipboardEvent} event - Clipboard event
 * @param {Object} params - Parameters
 * @param {boolean} params.canUploadImgToSendAi - Can upload image flag
 * @param {Object[]} params.uploadImgs - Current uploaded images
 * @param {Object[]} params.uploadFiles - Current uploaded files
 * @param {Function} params.setUploadImgs - Set uploaded images function
 * @param {Function} params.enqueueSnackbar - Snackbar function
 * @param {Object} params.corporationDetail - Corporation detail
 * @param {Object} params.projectInuseDetail - Project in use detail
 * @param {string} params.chatThreadId - Chat thread ID
 * @param {Function} params.setIsUploading - Set uploading state
 * @param {Object} params.inputRef - Input ref to focus after paste
 * @returns {boolean} - true if handled paste images, false otherwise
 */
const handlePasteImages = (event, params) => {
    const {
        canUploadImgToSendAi,
        uploadImgs,
        uploadFiles = [],
        setUploadImgs,
        enqueueSnackbar,
        corporationDetail,
        projectInuseDetail,
        chatThreadId,
        setIsUploading,
        inputRef
    } = params;

    const items = event.clipboardData.items;
    let listPastedImgs = [];

    for (const item of items) {
        if (item.kind === 'file') {
            const file = item.getAsFile();
            // Only collect image files
            if (file && isValidImageFile(file)) {
                listPastedImgs.push(file);
            }
        }
    }

    if (listPastedImgs.length && canUploadImgToSendAi) {
        const isValidImgs = validateUploadImages(listPastedImgs, uploadImgs, uploadFiles, enqueueSnackbar);
        if (!isValidImgs) {
            event.preventDefault();
            return true;
        }

        const createImgs = createTmpImageObjects(listPastedImgs);
        setUploadImgs(prev => [...prev, ...createImgs]);

        createTmpUploadedImgs(
            corporationDetail.id,
            projectInuseDetail.id,
            chatThreadId,
            createImgs,
            setUploadImgs,
            setIsUploading
        );

        event.preventDefault();
        if (inputRef?.current) {
            inputRef.current.focus();
        }
        return true;
    }

    return false;
};

/**
 * Remove uploaded image
 * @param {Object} removeImg - Image to remove
 * @param {Function} setUploadImgs - Set uploaded images function
 */
const removeUploadedImage = (removeImg, setUploadImgs) => {
    setUploadImgs(preImg => preImg.filter(img => img.id !== removeImg.id));
    removeImg.localUrl && URL.revokeObjectURL(removeImg.localUrl);
    updatePublicAPI(null, {
        updateApiType: Constant.UPDATE_API_PUBLIC_TYPE.DELETE_S3_FILE_BY_PATH,
        s3Path: removeImg.s3Path
    });
};

/**
 * Cleanup uploaded images (revoke object URLs)
 * @param {Object[]} uploadImgs - List of uploaded images
 * @param {Function} setUploadImgs - Set uploaded images function
 */
const cleanupUploadedImages = (uploadImgs, setUploadImgs) => {
    if (Utils.isEmptyArray(uploadImgs)) return;
    for (let img of uploadImgs) {
        img.localUrl && URL.revokeObjectURL(img.localUrl);
    }
    setUploadImgs([]);
};

/**
 * Check if device supports image capture (mobile camera)
 * @returns {boolean} - true if supports, false otherwise
 */
const checkImageCaptureSupport = () => {
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const testInput = document.createElement('input');
    testInput.type = 'file';
    const supportsCapture = 'capture' in testInput;
    return isMobile && supportsCapture;
};

const handleUploadCapturedImg = ({
    file,
    currentUploadImgs = [],
    currentUploadFiles = [],
    setUploadImgs,
    enqueueSnackbar,
    corporationDetail,
    projectInuseDetail,
    chatThreadId,
    setIsUploading,
}) => {
    if (!file) return;

    // Validate using strict image validation
    const result = validateImagesOnly({
        newFiles: [file],
        currentUploadImgs: currentUploadImgs,
        currentUploadFiles: currentUploadFiles,
        enqueueSnackbar
    });

    if (!result.isValid || result.imageFiles.length === 0) return;

    const img = {
        id: `tmp_attach_${uuidv4()}`,
        localUrl: URL.createObjectURL(file),
        fileData: file
    };
    setUploadImgs(prev => [...prev, img]);
    createTmpUploadedImgs(
        corporationDetail.id,
        projectInuseDetail.id,
        chatThreadId,
        [img],
        setUploadImgs,
        setIsUploading
    );
};

export {
    MAX_UPLOAD_COUNT,
    MAX_FILE_SIZE,
    VALID_IMAGE_TYPES,
    VALID_IMAGE_ACCEPT,
    validateUploadImages,
    createTmpImageObjects,
    handleDropImages,
    handlePasteImages,
    removeUploadedImage,
    cleanupUploadedImages,
    checkImageCaptureSupport,
    handleUploadCapturedImg
};
